/**
 * Toast Service - Global singleton for managing toast notifications
 * 
 * This service allows the API client and other utilities to show toast messages
 * without needing direct access to React context.
 */

class ToastService {
  constructor() {
    this.showToastFn = null;
  }

  /**
   * Register the showToast function from AppContext
   * Should be called when AppContext initializes
   * 
   * @param {Function} showToastFn - The showToast function from AppContext
   */
  register(showToastFn) {
    this.showToastFn = showToastFn;
    console.log('ToastService: showToast function registered');
  }

  /**
   * Show a toast notification
   * 
   * @param {string} message - The message to display
   * @param {string} type - Toast type: 'success', 'error', 'info', 'warning'
   * @param {number} duration - Duration in milliseconds
   */
  show(message, type = 'info', duration = 3000) {
    if (this.showToastFn) {
      this.showToastFn(message, type, duration);
    } else {
      // Fallback to console if toast service not registered yet
      console.warn('ToastService not registered yet. Message:', message);
      console[type === 'error' ? 'error' : 'log'](`[${type.toUpperCase()}]`, message);
    }
  }

  /**
   * Show success toast
   */
  success(message, duration = 3000) {
    this.show(message, 'success', duration);
  }

  /**
   * Show error toast
   */
  error(message, duration = 4000) {
    this.show(message, 'error', duration);
  }

  /**
   * Show info toast
   */
  info(message, duration = 3000) {
    this.show(message, 'info', duration);
  }

  /**
   * Show warning toast
   */
  warning(message, duration = 3500) {
    this.show(message, 'warning', duration);
  }
}

// Export singleton instance
export const toastService = new ToastService();

